import React, { useState, useEffect, useMemo, useRef } from 'react';
import styled from 'styled-components';
import { Modal } from '../comps';
import * as Yup from 'yup';
import { FormikProvider, useFormik, FormikProps } from 'formik';
import { Poppins, Spacer } from 'src/common';
import Button from 'src/components/form/Button';
import { Guard } from '../Guard';
import { useCache } from 'src/graphql';
import {
  UpsertOrganisationMutationVariables,
  useUpsertOrganisationMutation,
  WorkspaceCreateInput,
} from 'src/graphql/_generated';
import { Step1, Step2 } from './forms';
import { Stepper } from 'src/components/Wizzard/comps/Stepper';
import { useAuth } from 'src/state/auth';
import { get } from 'lodash';
import {
  workspaceValidationSchema,
  workspaceInitialValues,
  WorkspaceFormValues,
  WorkspaceForm,
} from 'src/components/formComposed/WorkspaceForm';

type OrgFormValues = Pick<
  UpsertOrganisationMutationVariables,
  'name' | 'domain' | 'primaryContact' | 'billingContact' | 'organisationType'
>;

const StyledModal = styled(Modal)`
  width: 566px;
  padding: 0;
  max-height: calc(100% - 40px);
  overflow-y: auto;

  .btns {
    display: grid;
    grid-template-columns: 1fr 1fr;
    grid-gap: 20px;
    .btn-prev {
      display: inline-flex;
      align-items: center;
      margin-right: auto;
      cursor: pointer;
      text-decoration: underline;
      text-decoration-color: ${({ theme }) => theme.colors.prussianBlue};
    }
  }

  .error {
    color: ${({ theme }) => theme.colors.error};
    font-size: 14px;
    text-align: center;
  }
`;

interface OrganisationCreateModalV2Props {
  onClose: () => void;
}

export const OrganisationCreateModalV2: React.FC<OrganisationCreateModalV2Props> = ({ onClose }) => {
  const [err, setErr] = useState('');
  const [isCloseRequested, setIsCloseRequested] = useState(false);
  const [step, setStep] = useState<1 | 2 | 3>(1);
  const [createWorkspace, setCreateWorkspace] = useState(true);
  const { refetchQuery } = useCache();
  const { flatOrganisations } = useAuth();

  const orgValidationSchema = useMemo(() => {
    const existingNames =
      flatOrganisations?.filter((o) => o.recordType === 'O').map((o) => o.name.trim().toLowerCase()) || [];

    return Yup.object({
      name: Yup.string()
        .required('Required')
        .max(200, 'Max 200 characters')
        .test('unique-name', 'Organisation name already exists', (value) => {
          if (!value) return true;
          return !existingNames.includes(value.trim().toLowerCase());
        }),
      domain: Yup.string()
        .required('Required')
        .matches(
          /^[a-zA-Z0-9]([a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(\.[a-zA-Z0-9]([a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*\.[a-zA-Z]{2,}$/,
          'Email domain should be a valid domain format (e.g., domain.com)',
        ),
      primaryContact: Yup.string().required('Required').email('Invalid email address').max(100, 'Max 100 characters'),
      billingContact: Yup.string().nullable().email('Invalid email address').max(100, 'Max 100 characters'),
    });
  }, [flatOrganisations]);

  const { profile } = useAuth();

  const [create] = useUpsertOrganisationMutation({
    onCompleted: () => {
      refetchQuery({ include: ['profile', 'flatOrganisations'] });
      onClose();
    },
    onError: (err) => setErr(err.message),
  });

  // Ref to store workspace formik instance (breaks circular dependency)
  const workspaceFormikRef = useRef<FormikProps<WorkspaceFormValues> | null>(null);

  const orgFormik = useFormik<OrgFormValues>({
    initialValues: {
      name: '',
      primaryContact: '',
      domain: '',
    },
    validationSchema: orgValidationSchema,
    validateOnMount: true,
    enableReinitialize: true,
    onSubmit: async (values): Promise<void> => {
      let workspaceInput: WorkspaceCreateInput | undefined;
      if (createWorkspace && workspaceFormikRef.current) {
        const wsValues = workspaceFormikRef.current.values;
        workspaceInput = {
          name: wsValues.name,
          primaryContact: wsValues.primaryContact,
          plan: wsValues.plan,
          isRestricted: wsValues.isRestricted,
          currency: wsValues.currency,
        };
      }

      await create({
        variables: { ...values, createWorkspace, workspace: workspaceInput },
      });
    },
  });

  // Workspace form - validation schema without organisationId requirement (since it's auto-populated)
  const workspaceValidationSchemaForOrgContext = workspaceValidationSchema.shape({
    organisationId: Yup.string(), // Not required since org doesn't exist yet
  });

  const workspaceFormik = useFormik<WorkspaceFormValues>({
    initialValues: {
      ...workspaceInitialValues,
      primaryContact: '',
    },
    validationSchema: workspaceValidationSchemaForOrgContext,
    validateOnMount: true,
    enableReinitialize: false,
    onSubmit: () => {
      // Workspace submission is handled by org form submission
    },
  });

  // Keep ref in sync with formik instance
  workspaceFormikRef.current = workspaceFormik;

  useEffect(() => {
    if (orgFormik.values.primaryContact && !workspaceFormik.touched.primaryContact) {
      workspaceFormik.setFieldValue('primaryContact', orgFormik.values.primaryContact);
    }
  }, [orgFormik.values.primaryContact]);

  const {
    dirty: orgDirty,
    handleSubmit,
    values: orgValues,
    errors: orgErrors,
    isValid: orgIsValid,
    isSubmitting,
  } = orgFormik;
  const { dirty: workspaceDirty, isValid: workspaceIsValid } = workspaceFormik;

  const selectedSettingsOption = profile?.orgSettingsOptions?.find(
    (o) => o.organisationType === orgValues.organisationType,
  );

  const canNextStep = !get(orgErrors, 'domain') && !get(orgErrors, 'primaryContact') && !get(orgErrors, 'name');

  const stepperSlides = createWorkspace
    ? [
        { title: '', id: '1' },
        { title: '', id: '2', locked: !canNextStep },
        { title: '', id: '3', locked: !canNextStep },
      ]
    : [
        { title: '', id: '1' },
        { title: '', id: '2', locked: !canNextStep },
      ];

  const maxSteps = createWorkspace ? 3 : 2;

  const isDirty = orgDirty || (createWorkspace && workspaceDirty);
  const isCreateDisabled = createWorkspace
    ? !orgIsValid || !workspaceIsValid || isSubmitting
    : !orgIsValid || isSubmitting;

  return (
    <Guard
      onCancel={() => setIsCloseRequested(false)}
      onClose={onClose}
      isOpen={isCloseRequested}
      isDirty={isDirty}
      modalComponent={
        <StyledModal onRequestClose={() => setIsCloseRequested(true)} isOpen>
          <Spacer $px={40} />
          <div className="h-padding">
            <Poppins className="m-title" px={28}>
              {step === 3 ? 'Create a Workspace' : 'Create an Organisation'}
            </Poppins>
            <Spacer $px={20} />
            <div style={{ width: createWorkspace ? 270 : 180 }}>
              <Stepper
                slides={stepperSlides}
                maxSteps={maxSteps}
                step={step - 1}
                onChangeSlide={(id) => setStep(Number(id) as 1 | 2 | 3)}
              />
            </div>
            <Spacer $px={20} />

            {/* Step 1 and 2 use org formik */}
            <FormikProvider value={orgFormik}>
              {step === 1 && <Step1 />}
              {step === 2 && (
                <Step2
                  selectedSettingsOption={selectedSettingsOption}
                  createWorkspace={createWorkspace}
                  onCreateWorkspaceChange={setCreateWorkspace}
                />
              )}
            </FormikProvider>

            {/* Step 3 uses workspace formik */}
            {step === 3 && (
              <FormikProvider value={workspaceFormik}>
                <WorkspaceForm disableOrganisationSelect organisationDisplayName={orgValues.name || ''} />
              </FormikProvider>
            )}

            <Spacer $px={40} />
            {step === 1 && (
              <Button onClick={() => setStep(2)} primary style={{ width: '100%' }} disabled={!canNextStep} data-cy="org-proceed-btn">
                PROCEED
              </Button>
            )}
            {step === 2 && (
              <div className="btns">
                <div className="btn-prev" onClick={() => setStep(1)}>
                  <Poppins weight={500}>Previous</Poppins>
                </div>
                {createWorkspace ? (
                  <Button onClick={() => setStep(3)} primary data-cy="org-proceed-btn">
                    PROCEED
                  </Button>
                ) : (
                  <Button onClick={() => handleSubmit()} primary disabled={isCreateDisabled} data-cy="org-submit-btn">
                    Create
                  </Button>
                )}
              </div>
            )}
            {step === 3 && (
              <div className="btns">
                <div className="btn-prev" onClick={() => setStep(2)}>
                  <Poppins weight={500}>Previous</Poppins>
                </div>
                <Button onClick={() => handleSubmit()} primary disabled={isCreateDisabled} data-cy="org-submit-btn">
                  Create
                </Button>
              </div>
            )}

            {!!err && (
              <>
                <Spacer $px={15} />
                <div className="error">{err}</div>
              </>
            )}
          </div>

          <Spacer $px={40} />
        </StyledModal>
      }
    />
  );
};
